import moment from "moment";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useViewEvChargerSessionGraphQuery,
  useViewEvChargerSessionListQuery,
} from "../../../redux/api/ev-charger/evChargerAPI";
import ChargeHistoryCard from "../../../shared/components/charge-history-card/charge-history-card";
import Reveal from "../../../shared/components/reveal/reveal";
import { EEvGraphViewType } from "../../../shared/oversight-core/enums/ev-graph-view-type";
import AppDatePicker from "../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import AppTabs, {
  TabComponentInterface,
} from "../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import BarChart from "../../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import Pagination from "../../../shared/oversight-core/ui-elements/pagination/pagination";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  calculateDateRange,
  formatGraphPointLabel,
  getDatePickerFormat,
  getGraphDateDisplay,
  getViewType,
  getXAxisLabel,
} from "../../../shared/oversight-core/utils/date-utils";
import Helper from "../../../shared/oversight-core/utils/helpers";
import ExportChargingHistoryModal from "./components/export-charging-history-modal/export-charging-history-modal";
import styles from "./history.module.scss";

const tabs: TabComponentInterface[] = [
  { id: 1, buttonName: "Day" },
  { id: 2, buttonName: "Month" },
  { id: 3, buttonName: "Year" },
];

const History = () => {
  const [selectedTabId, setSelectedTabId] = useState<number>(2);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const itemsPerPage = 5;

  const viewType = getViewType(selectedTabId);
  const { startDate, endDate } = calculateDateRange(selectedDate, viewType);

  const { data: graphPoints = [], isFetching: isFetchingGraph } =
    useViewEvChargerSessionGraphQuery({
      startDate,
      endDate,
      viewType,
      zoneOffset: "Asia/Colombo",
    });

  const { data: sessionListResponse, isFetching: isFetchingSessions } =
    useViewEvChargerSessionListQuery({
      pageNumber: currentPage - 1,
      pageSize: itemsPerPage,
    });

  const chartLabels =
    graphPoints.length > 0
      ? graphPoints.map((point) =>
          formatGraphPointLabel(point.periodStart, viewType),
        )
      : [];

  const chartData =
    graphPoints.length > 0
      ? graphPoints.map((point) => point.totalEnergyConsumed ?? 0)
      : [];

  const totalEnergy = chartData.reduce((acc, curr) => acc + curr, 0);
  const dateDisplay = getGraphDateDisplay(selectedDate, viewType);
  const dateFormat = getDatePickerFormat(viewType);

  return (
    <div className="position-relative">
      <Reveal className={styles.filtersRow}>
        <Row className="align-items-center mb-4">
          <Col>
            <Row className="align-items-center g-3">
              <Col xs={12} sm={6} md={5} lg={4}>
                <AppTabs
                  buttons={tabs}
                  onChange={setSelectedTabId}
                  selectedTabId={selectedTabId}
                />
              </Col>
              <Col xs={12} sm={6} md={5} lg={4} xl={3}>
                <AppDatePicker
                  selectedDate={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat={dateFormat}
                  monthYearPicker={viewType === EEvGraphViewType.DAY}
                  yearPicker={viewType === EEvGraphViewType.MONTH}
                  showDate={viewType === EEvGraphViewType.HOUR}
                />
              </Col>
            </Row>
          </Col>
          <Col className="col-auto">
            <AppButton
              text=""
              type="button"
              variant="secondary"
              iconName="file_export"
              iconOnly={true}
              iconColor="var(--icon-color)"
              iconSize={22}
              className={styles.historyExportBtn}
              onClick={() => setShowExportModal(true)}
              title="Export History"
            />
          </Col>
        </Row>
      </Reveal>
      <Reveal delay={70}>
        <div
          className={`d-flex flex-wrap align-items-center justify-content-center text-center mb-4 bg-white rounded-4 gap-2 gap-sm-3 p-3 ${styles.energySummary}`}
        >
          <span className="font-size-24 font-weight-500 text-dark">
            {Number(Helper.roundTo2(totalEnergy))} kWh
          </span>
          <span className="font-size-16 text-light">{dateDisplay}</span>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="mb-5 position-relative">
          <BarChart
            labels={chartLabels}
            data1={chartData}
            backgroundColor1="#6b72cd"
            borderColor1="#6b72cd"
            yAxesUnit="kWh"
            xAxisTitle={getXAxisLabel(viewType)}
            isChartDataAvailable={chartData.length > 0}
            borderRadius={4}
            barThickness={10}
            height="400px"
            minHeight="400px"
            width="100%"
          />
        </div>
      </Reveal>
      <div>
        <Reveal delay={160}>
          <div className="text-dark mb-3 font-weight-600">Charge History</div>
        </Reveal>
        {sessionListResponse?.sessions.elements &&
        sessionListResponse.sessions.elements.length > 0 ? (
          <div>
            {sessionListResponse.sessions.elements.map((item, index) => (
              <Reveal key={index} delay={Math.min(index, 8) * 50}>
                <ChargeHistoryCard
                  dateRange={`${
                    item.startTime
                      ? moment(item.startTime).format("MMM DD HH:mm")
                      : ""
                  } - ${
                    item.stopTime
                      ? moment(item.stopTime).format("MMM DD HH:mm")
                      : ""
                  }`}
                  chargerName={item.chargeBoxName || ""}
                  userName={item.consumerName || ""}
                  id={`#${item.transactionId || ""}`}
                  usage={
                    item.energyConsumed != null
                      ? Number(Helper.roundTo2(Number(item.energyConsumed)))
                      : 0
                  }
                />
              </Reveal>
            ))}
            <Pagination
              length={sessionListResponse?.sessions.totalElements || 0}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              updateCurrentPage={setCurrentPage}
            />
          </div>
        ) : (
          <Row className="container-dash mx-0 mt-4">
            <Col className="text-center font-weight-40 text-light">
              There is no historical data
            </Col>
          </Row>
        )}
      </div>
      <SpinnerModal show={isFetchingSessions || isFetchingGraph} />
      <ExportChargingHistoryModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
};

export default History;
